import { MagnifyingGlass } from '@medusajs/icons';
import { Button, Input, StatusBadge, Text } from '@medusajs/ui';
import { normalizeCui } from '@pcrm/shared/cui';
import { useRef, useState } from 'react';

/**
 * Câmp de CUI cu căutarea firmei la ANAF, pentru precompletarea formularului din jur.
 *
 * ## Componenta nu face call-ul
 *
 * Primește `onLookup` și atât. Nu e preferință de stil: clientul de API stă într-o
 * **aplicație** (`apps/crm/src/lib/api.ts`), iar `@nx/enforce-module-boundaries` interzice
 * unei biblioteci `type:lib` să depindă de una `type:app` — `pnpm verify:boundaries` ar
 * cădea. Nici URL-ul dat ca prop n-ar ajuta: apelul are nevoie de prefixul de origine, de
 * `credentials: 'include'` (sesiunea e cookie `HttpOnly`), de Zod pe răspuns și de
 * traducerea statusului HTTP în taxonomia de mai jos — adică de tot clientul, mutat aici
 * bucată cu bucată.
 *
 * Consecința bună e chiar refolosirea: la firmele grupului și la clienții persoane juridice
 * se caută la fel, dar rezultatul se mapează pe câmpuri diferite. Componenta nu știe pe
 * care — predă firma în sus prin `onResult` și se oprește.
 *
 * ## Starea e a ei
 *
 * Cele patru stări (inactiv, se caută, găsit, eroare) le ține componenta, derivate din
 * promisiunea primită. Dacă le-ar primi ca props de la un `useQuery` al apelantului, ar
 * exista două surse de adevăr și componenta n-ar mai merge într-un formular fără TanStack
 * Query. Cine vrea cache îl pune în `onLookup` (`queryClient.fetchQuery`) și tot un
 * `Promise` întoarce.
 */

/**
 * De ce sunt patru coduri și nu un singur „a eșuat": operatorul ia o decizie diferită la
 * fiecare. La `invalid_cui` retastează, la `not_found` verifică sursa, la `unavailable`
 * completează manual și merge mai departe. Un mesaj comun l-ar lăsa să ghicească.
 */
export type CuiLookupErrorCode = 'invalid_cui' | 'not_found' | 'unavailable' | 'unknown';

/**
 * Eroarea pe care o aruncă `onLookup`. Apelantul traduce ce știe el (status HTTP, cod de
 * la server) în codul de aici; componenta nu vede niciodată un `Response`.
 */
export class CuiLookupError extends Error {
  constructor(
    readonly code: CuiLookupErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'CuiLookupError';
  }
}

/** Sediul social, cum vine de la ANAF. Câmpurile chiar lipsesc uneori — de aceea `null`. */
export interface CuiLookupAddress {
  readonly street: string | null;
  readonly city: string | null;
  readonly county: string | null;
  /**
   * Codul județului din lista închisă (`PH`, `B`), când serverul îl recunoaște. Opțional:
   * doar fișa firmei îl folosește (#183); partenerii păstrează județul ca text.
   */
  readonly countyCode?: string | null;
  readonly postalCode: string | null;
}

/**
 * Firma găsită.
 *
 * Tipul e declarat aici, nu importat din backend: `ui-kit` e `scope:fe` și n-are voie să
 * depindă de `@pcrm/be/anaf`. Forma e aceeași, iar potrivirea o verifică TypeScript la
 * locul apelului — schema Zod din aplicație trebuie să producă exact asta.
 */
export interface CuiLookupCompany {
  /** CUI-ul normalizat, fără `RO` — forma în care se salvează. */
  readonly cui: string;
  readonly name: string;
  readonly tradeRegisterNo: string | null;
  readonly address: CuiLookupAddress;
  readonly phone: string | null;
  readonly vatPayer: boolean;
  readonly vatOnCollection: boolean;
  readonly status: 'active' | 'inactive' | 'struck_off';
  /** Starea în cuvintele ANAF: „RADIERE din data 29.10.1998". */
  readonly statusText: string | null;
  /** Când s-a citit răspunsul, ISO 8601. Poate fi mai vechi: răspunsul vine din cache. */
  readonly checkedAt: string;
}

export interface CuiLookupProps {
  /** Al câmpului, ca formularul din jur să-și poată lega eticheta cu `htmlFor`. */
  readonly id?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Caută firma. Primește CUI-ul deja normalizat (doar cifre, fără `RO`). */
  readonly onLookup: (cui: string) => Promise<CuiLookupCompany>;
  /** Firma găsită, predată formularului. Maparea pe câmpuri e a lui. */
  readonly onResult: (company: CuiLookupCompany) => void;
  readonly disabled?: boolean;
  /** Câmpul a fost respins de formular (ex. „CIF deja folosit"). */
  readonly invalid?: boolean;
  /**
   * Formularul nu se poate trimite fără el. Steluța o pune eticheta, care e a formularului;
   * aici e doar `aria-required`, ca cititorul de ecran să anunțe regula, nu un asterisc.
   */
  readonly required?: boolean;
  readonly placeholder?: string;
}

type LookupState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'searching' }
  | { readonly kind: 'found'; readonly company: CuiLookupCompany }
  | { readonly kind: 'error'; readonly code: CuiLookupErrorCode; readonly message?: string };

const ERROR_MESSAGES: Record<CuiLookupErrorCode, string> = {
  invalid_cui: 'Codul fiscal nu e valid — cifra de control nu se potrivește. Verifică cifrele.',
  not_found: 'ANAF nu are nicio firmă înregistrată pe codul ăsta.',
  unavailable: 'ANAF nu răspunde acum. Completează câmpurile manual — nu e nimic greșit la tine.',
  unknown: 'Căutarea la ANAF a eșuat.',
};

/**
 * `RO` + 10 cifre + separatoarele pe care le pune omul. Peste atât nu e un CUI, e altceva
 * lipit în câmp.
 */
const MAX_LENGTH = 16;

export function CuiLookup({
  id,
  value,
  onChange,
  onLookup,
  onResult,
  disabled = false,
  invalid = false,
  required = false,
  placeholder,
}: CuiLookupProps) {
  const [state, setState] = useState<LookupState>({ kind: 'idle' });

  /**
   * Numărul cererii curente. Fără el, un răspuns lent pentru CUI-ul de acum două tastări
   * ar ateriza peste ce vede operatorul acum — adică ar precompleta formularul cu altă
   * firmă decât cea de pe ecran.
   */
  const ticket = useRef(0);

  const search = () => {
    const cui = normalizeCui(value);

    // Validarea locală e singura care poate spune „ai greșit o cifră": ANAF întoarce
    // pentru un CUI cu cifra de control greșită exact același răspuns ca pentru unul
    // inexistent. Un cod greșit nici nu iese pe rețea.
    if (cui === null) {
      setState({ kind: 'error', code: 'invalid_cui' });
      return;
    }

    const current = ++ticket.current;
    setState({ kind: 'searching' });

    onLookup(cui).then(
      (company) => {
        if (current !== ticket.current) {
          return;
        }
        setState({ kind: 'found', company });
        onResult(company);
      },
      (error: unknown) => {
        if (current !== ticket.current) {
          return;
        }
        setState({
          kind: 'error',
          code: error instanceof CuiLookupError ? error.code : 'unknown',
          message: error instanceof CuiLookupError ? error.message : undefined,
        });
      },
    );
  };

  const handleChange = (next: string) => {
    // Orice tastă anulează verdictul de dinainte, și pe cel în zbor: „găsit: Vodafone"
    // rămas sub un CUI schimbat între timp e o minciună pe ecran.
    ticket.current++;
    setState({ kind: 'idle' });
    onChange(next);
  };

  const searching = state.kind === 'searching';

  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex items-center gap-x-2">
        <Input
          id={id}
          value={value}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={(event) => {
            // Căutarea e explicită — la buton sau la Enter. **Fără căutare la tastare:**
            // un `useEffect` cu debounce e cel mai simplu mod de a lua 429 de la ANAF.
            // `preventDefault` ca Enter să nu trimită formularul din jur.
            if (event.key === 'Enter') {
              event.preventDefault();
              search();
            }
          }}
          disabled={disabled}
          maxLength={MAX_LENGTH}
          placeholder={placeholder}
          className="font-mono"
          aria-required={required || undefined}
          aria-invalid={invalid || state.kind === 'error' ? true : undefined}
          autoComplete="off"
        />
        <Button
          type="button"
          variant="secondary"
          onClick={search}
          isLoading={searching}
          // Butonul rămâne activ pe un CUI greșit, dinadins: apăsat, spune *ce* e greșit.
          // Dezactivat, ar lăsa operatorul să se uite la un buton mort fără să afle de ce.
          disabled={disabled || searching || value.trim().length === 0}
        >
          <MagnifyingGlass />
          Caută la ANAF
        </Button>
      </div>

      <LookupStatus state={state} />
    </div>
  );
}

/** Ce s-a întâmplat cu ultima căutare. Nimic, cât timp nu s-a căutat. */
function LookupStatus({ state }: { state: LookupState }) {
  if (state.kind === 'idle') {
    return null;
  }

  if (state.kind === 'searching') {
    return (
      <Text size="small" className="text-ui-fg-subtle" role="status">
        Se caută la ANAF…
      </Text>
    );
  }

  if (state.kind === 'error') {
    return (
      <Text size="small" className="text-ui-fg-error" role="alert">
        {/* Mesajul serverului trece înaintea celui generic doar pentru `unknown` — acolo
            n-avem noi nimic mai bun de spus, iar „sesiunea a expirat" e exact genul de
            motiv pe care operatorul trebuie să-l vadă întreg. */}
        {state.code === 'unknown' && state.message ? state.message : ERROR_MESSAGES[state.code]}
      </Text>
    );
  }

  const { company } = state;

  return (
    <div className="flex flex-col gap-y-1" role="status">
      <Text size="small" className="text-ui-fg-subtle">
        Găsit la ANAF: <span className="text-ui-fg-base">{company.name}</span>
      </Text>

      {/*
        Firma neactivă **nu se ascunde și nu blochează** precompletarea. Operatorul are
        voie să continue — dar contractul pe care îl pregătește e cu o firmă radiată sau
        inactivă fiscal, iar asta e cea mai importantă informație din tot răspunsul.
        Textul de la ANAF se arată ca atare: `status` e interpretarea noastră, iar
        formularea lor poate spune mai mult decât am prins noi în trei valori.
      */}
      {company.status !== 'active' ? (
        <div className="flex items-center gap-x-2">
          <StatusBadge color={company.status === 'struck_off' ? 'red' : 'orange'}>
            {company.status === 'struck_off' ? 'Radiată' : 'Inactivă fiscal'}
          </StatusBadge>
          {company.statusText ? (
            <Text size="small" className="text-ui-fg-subtle">
              {company.statusText}
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
